import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { clientApi } from "../../api";
import PasswordInput from "../../components/PasswordInput";
import { formatDateAU } from "../../utils/formatDate";

/**
 * PIN-verified lookup — the front-desk tool for "this customer wants a
 * PIN-gated benefit (reward redemption, tier unlock, etc.)". Calling
 * `/members/by-card/verify` hits S7000 `getcardholderdetails` which only
 * returns details when the PIN is correct. One round-trip; no details
 * leak on wrong PIN.
 */
export default function PinLookup() {
  const navigate = useNavigate();
  const [cardNumber, setCardNumber] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function verify(e) {
    e.preventDefault();
    setError(""); setResult(null);
    if (!cardNumber && !memberNumber) return setError("Enter card number or membership number.");
    if (!/^\d{3,10}$/.test(pin)) return setError("PIN must be 3–10 digits.");
    setBusy(true);
    try {
      const r = await clientApi.verifyCardholderPin({ cardNumber, memberNumber, pin });
      // Response is always 200 now. Valid / invalid is in the payload.
      if (r && r.valid === false) {
        setError(r.message || "Verification failed");
      } else {
        setResult(r);
      }
    } catch (err) {
      setError(err.message || "Verification failed");
    }
    setBusy(false);
  }

  function clear() {
    setCardNumber(""); setMemberNumber(""); setPin(""); setResult(null); setError("");
  }

  const m = result?.member?.member || result?.member || null;

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>PIN-verified Lookup</h1>
          <p className="page-subtitle">Confirm identity at the counter for PIN-gated benefits</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 420px) minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
        <form onSubmit={verify} className="settings-card">
          <div className="settings-card-header">
            <h3>Identify member</h3>
            <p>Scan or type the card, or enter membership number + PIN</p>
          </div>

          {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

          <div className="form-field">
            <label>Card number</label>
            <input
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/\s+/g, ""))}
              autoFocus
              placeholder="Scan card"
            />
          </div>

          <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem", margin: "0.25rem 0" }}>or</div>

          <div className="form-field">
            <label>Membership number</label>
            <input
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 56153"
              inputMode="numeric"
            />
          </div>

          <div className="form-field">
            <label>PIN <span className="required">*</span></label>
            <PasswordInput
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              inputMode="numeric"
              pattern="\d*"
              required
              placeholder="3–10 digits"
            />
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Checking…" : "Verify"}</button>
            <button type="button" className="btn btn-outline" onClick={clear}>Clear</button>
          </div>
        </form>

        <div>
          {!result ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              <h3>Awaiting verification</h3>
              <p>Nothing is shown until the member's PIN is confirmed — this prevents identity leak on wrong PIN.</p>
            </div>
          ) : (
            <div className="settings-card">
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                <div style={{ width: 56, height: 56, borderRadius: 12, background: "var(--success-bg, #10b98122)", color: "var(--success, #10b981)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="24" height="24"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>Verified</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>PIN matches S7000</div>
                </div>
              </div>

              {m && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.5rem" }}>
                  <Field label="Name"          value={`${m.firstName || ""} ${m.lastName || ""}`.trim() || "—"} />
                  <Field label="Membership"    value={m.membershipId ? `#${m.membershipId}` : "—"} />
                  <Field label="Card"          value={m.cardNumber || m.barCode || cardNumber || "—"} />
                  <Field label="Email"         value={m.email || "—"} />
                  <Field label="Mobile"        value={m.mobile || "—"} />
                  <Field label="Expiry"        value={m.dateExpiry ? formatDateAU(m.dateExpiry) : "—"} />
                  <Field label="Status"        value={String(m.memberStatus || m.suspended === "1" ? "Suspended" : "Active")} />
                </div>
              )}

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
                {m?.membershipId && (
                  <button className="btn btn-primary" onClick={() => navigate(`/client/members/${m.membershipId}`)}>
                    Open full profile
                  </button>
                )}
                <button className="btn btn-outline" onClick={clear}>Next member</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}

function Field({ label, value }) {
  return (
    <div className="stat-card" style={{ padding: "0.6rem 0.8rem" }}>
      <span className="stat-label">{label}</span>
      <span style={{ fontSize: "0.9rem", color: "var(--text)", marginTop: "0.25rem" }}>{value}</span>
    </div>
  );
}
